import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Calendar, Clock, Download, Printer, FileDown } from 'lucide-react';
import { SidebarProvider } from "@/components/ui/sidebar";
import Teacherheader from "./Teacherheader";
import Teachersidebar from "./Teachersidebar";
import SuspensionWarning from "./SuspensionWarning";
import useSuspensionCheck from "./useSuspensionCheck";
import { db, auth } from "../firebase/config";
import { collection, onSnapshot, doc, getDoc, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";

interface TimetableEntry {
  teacherId: string;
  teacherName: string;
  classId: string;
  className: string;
  subjectId: string;
  subjectName: string;
}

interface Teacher {
  id: string;
  name: string;
  uid?: string;
}

interface Period {
  time: string;
  subject: string;
  class: string;
  teacher?: string;
}

const TeacherTimetable = () => {
  // Check suspension status
  const { isSuspended, isLoading: suspensionLoading, schoolName: suspendedSchoolName } = useSuspensionCheck();

  const [viewMode, setViewMode] = useState<'my' | 'class'>('my');
  const [selectedClass, setSelectedClass] = useState('');
  const [viewType, setViewType] = useState<'weekly' | 'daily'>('weekly');
  const [selectedDay, setSelectedDay] = useState('Monday');
  
  // Firebase states
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [schoolName, setSchoolName] = useState('Excellence Academy');
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [networkError, setNetworkError] = useState<boolean>(false);
  const [user, setUser] = useState<any>(null);
  const [myId, setMyId] = useState<string | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [classes, setClasses] = useState<{id: string, name: string}[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [publishedTimetables, setPublishedTimetables] = useState<Record<string, {schedule: Record<string, TimetableEntry>}>>({});
  
  const currentTerm = 'Term 2';
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  // Authentication logic
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          await currentUser.getIdToken(true);
          const idTokenResult = await currentUser.getIdTokenResult(true);
          if (!idTokenResult) {
            throw new Error("Failed to retrieve user token claims");
          }
          const affiliations = (idTokenResult.claims.affiliations as { role: string; schoolID?: string }[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as { role: string; schoolID?: string } | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          let selectedAffiliation: { role: string; schoolID?: string } | null = null;
          if (defaultAffiliation && ['teacher', 'school_staff', 'admin'].includes(defaultAffiliation.role) && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => ['teacher', 'school_staff', 'admin'].includes(aff.role) && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role && ['teacher', 'school_staff', 'admin'].includes(selectedAffiliation.role) && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            setSchoolID(selectedAffiliation.schoolID);
            setHasValidClaims(true);
            setNetworkError(false);
          } else {
            await new Promise(resolve => setTimeout(resolve, 10000));
            await currentUser.getIdToken(true);
            const retryIdTokenResult = await currentUser.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as { role: string; schoolID?: string }[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as { role: string; schoolID?: string } | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            let retrySelectedAffiliation: { role: string; schoolID?: string } | null = null;
            if (retryDefaultAff && ['teacher', 'school_staff', 'admin'].includes(retryDefaultAff.role) && retryDefaultAff.schoolID) {
              retrySelectedAffiliation = retryDefaultAff;
            } else if (retryAffiliations.length > 0) {
              retrySelectedAffiliation = retryAffiliations.find(aff => ['teacher', 'school_staff', 'admin'].includes(aff.role) && aff.schoolID) || retryAffiliations[0];
            }

            if (retrySelectedAffiliation?.role && ['teacher', 'school_staff', 'admin'].includes(retrySelectedAffiliation.role) && retrySelectedAffiliation.schoolID && retrySchoolID === retrySelectedAffiliation.schoolID) {
              setSchoolID(retrySelectedAffiliation.schoolID);
              setHasValidClaims(true);
              setNetworkError(false);
            } else {
              setHasValidClaims(false);
              setSchoolID(null);
              setNetworkError(false);
            }
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          if (error.code === 'auth/network-request-failed' || error.message.includes('network')) {
            setNetworkError(true);
          } else {
            setHasValidClaims(false);
            setSchoolID(null);
            setNetworkError(false);
          }
        }
      } else {
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
        setNetworkError(false);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fetch school name
  useEffect(() => {
    if (!schoolID) return;
    const schoolRef = doc(db, 'schools', schoolID);
    getDoc(schoolRef).then((snap) => {
      if (snap.exists()) {
        setSchoolName(snap.data()?.name || 'Excellence Academy');
      }
    }).catch(console.error);
  }, [schoolID]);

  // Fetch teachers and set myId
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims || !user) return;
    const teachersRef = collection(db, `schools/${schoolID}/teachers`);
    const unsubscribe = onSnapshot(teachersRef, (snapshot) => {
      const teacherList = snapshot.docs.map(doc => {
        const data = doc.data();
        const middleName = data.middleName || '';
        return {
          id: doc.id,
          name: `${data.firstName} ${middleName} ${data.lastName}`.trim(),
          uid: data.uid,
        };
      });
      setTeachers(teacherList);
      if (user.uid) {
        const myTeacher = teacherList.find(t => t.uid === user.uid);
        setMyId(myTeacher?.id || null);
      }
    }, (error) => {
      console.error("Error fetching teachers:", error);
      setTeachers([]);
    });
    return () => unsubscribe();
  }, [schoolID, isLoading, hasValidClaims, user]);

  // Fetch classes
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) return;
    const classesRef = collection(db, `schools/${schoolID}/classes`);
    const unsubscribe = onSnapshot(classesRef, (snapshot) => {
      const classList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
      })).sort((a, b) => a.name.localeCompare(b.name));
      setClasses(classList);
    }, (error) => {
      console.error("Error fetching classes:", error);
      setClasses([]);
    });
    return () => unsubscribe();
  }, [schoolID, isLoading, hasValidClaims]);

  // Fetch sessions
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) return;
    const sessionsRef = doc(db, `schools/${schoolID}/config/sessions`);
    const unsubscribe = onSnapshot(sessionsRef, async (docSnap) => {
      if (!docSnap.exists()) {
        const defaultSessions = [
          { id: 1, name: "Period 1", startTime: "08:00", endTime: "09:00" },
          { id: 2, name: "Period 2", startTime: "09:00", endTime: "10:00" },
          { id: 3, name: "Break", startTime: "10:00", endTime: "10:30" },
          { id: 4, name: "Period 3", startTime: "10:30", endTime: "11:30" },
          { id: 5, name: "Period 4", startTime: "11:30", endTime: "12:30" },
          { id: 6, name: "Lunch", startTime: "12:30", endTime: "13:30" },
          { id: 7, name: "Period 5", startTime: "13:30", endTime: "14:30" },
          { id: 8, name: "Period 6", startTime: "14:30", endTime: "15:30" },
        ];
        await setDoc(sessionsRef, { sessions: defaultSessions }, { merge: true });
        setSessions(defaultSessions);
        return;
      }
      setSessions(docSnap.data().sessions || []);
    }, (error) => {
      console.error("Error fetching sessions:", error);
      setSessions([]);
    });
    return () => unsubscribe();
  }, [schoolID, isLoading, hasValidClaims]);

  // Fetch published timetables
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) return;
    const publishedRef = collection(db, `schools/${schoolID}/timetablePublished`);
    const unsubscribe = onSnapshot(publishedRef, (snapshot) => {
      const timetables: Record<string, {schedule: Record<string, TimetableEntry>}> = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.status === 'published') {
          timetables[doc.id] = { schedule: data.schedule || {} };
        }
      });
      setPublishedTimetables(timetables);
    }, (error) => {
      console.error("Error fetching published timetables:", error);
    });
    return () => unsubscribe();
  }, [schoolID, isLoading, hasValidClaims]);

  // Compute current schedule
  const currentSchedule = useMemo(() => {
    if (!publishedTimetables || !sessions || (viewMode === 'my' && !myId)) {
      return days.map(d => ({ day: d, periods: [] }));
    }

    const isMy = viewMode === 'my';
    const targetId = isMy ? myId : selectedClass;
    if (!targetId) {
      return days.map(d => ({ day: d, periods: [] }));
    }

    // Sort sessions by ID to ensure correct order
    const sortedSessions = [...sessions].sort((a, b) => a.id - b.id);

    return days.map(day => {
      const periods: Period[] = [];
      
      sortedSessions.forEach(session => {
        const time = `${session.startTime}-${session.endTime}`;
        const isBreak = session.isBreak || session.name.toLowerCase().includes('break') || session.name.toLowerCase().includes('lunch');
        
        if (isBreak) {
           periods.push({
             time,
             subject: session.name,
             class: '',
             teacher: ''
           });
           return;
        }

        let foundEntry: TimetableEntry | null = null;
        
        if (isMy) {
           // Search across all classes for this teacher at this time
           const classIds = Object.keys(publishedTimetables);
           for (const classId of classIds) {
             const schedule = publishedTimetables[classId]?.schedule;
             if (schedule) {
               const key = `${session.id}-${day}`;
               const entry = schedule[key];
               if (entry && entry.teacherId === myId) {
                 foundEntry = entry;
                 break;
               }
             }
           }
        } else {
           // Search for this class at this time
           const schedule = publishedTimetables[selectedClass]?.schedule;
           if (schedule) {
             const key = `${session.id}-${day}`;
             const entry = schedule[key];
             if (entry) {
               foundEntry = entry;
             }
           }
        }

        if (foundEntry) {
           periods.push({
             time,
             subject: foundEntry.subjectName,
             class: isMy ? foundEntry.className : (classes.find(c => c.id === selectedClass)?.name || selectedClass),
             teacher: isMy ? undefined : foundEntry.teacherName
           });
        } else {
           // Push empty period for non-break sessions with no assignment
           periods.push({
             time,
             subject: '',
             class: '',
             teacher: ''
           });
        }
      });

      return { day, periods };
    });
  }, [viewMode, selectedClass, publishedTimetables, sessions, myId, classes, days]);

  // Unique subjects for legend
  const uniqueSubjects = useMemo(() => {
    // Exclude 'Break', 'Lunch', and empty subjects from the legend
    return [...new Set(currentSchedule.flatMap(d => d.periods.map(p => p.subject)))].filter(
      s => s && !/break|lunch/i.test(s)
    );
  }, [currentSchedule]);

  // Subject colors
  const subjectColors = useMemo(() => {
    const colors = ['blue', 'purple', 'green', 'red', 'yellow', 'indigo', 'pink', 'cyan', 'orange'];
    return uniqueSubjects.reduce((acc, subject, idx) => {
      acc[subject] = colors[idx % colors.length];
      return acc;
    }, {} as Record<string, string>);
  }, [uniqueSubjects]);

  const availableClasses = useMemo(() => classes.filter(c => publishedTimetables[c.id]), [classes, publishedTimetables]);

  const getSubjectColor = (subject: string) => {
    const color = subjectColors[subject] || 'gray';
    // Always use bold, dark text for light backgrounds
    return `bg-${color}-100 text-gray-900 font-bold dark:bg-${color}-900 dark:text-${color}-100 dark:font-bold`;
  };

  const now = new Date();
  const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
  const currentTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  const timeToMinutes = (timeStr: string): number => {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };

  const getNextClass = () => {
    const todaySchedule = currentSchedule.find(d => d.day === currentDay);
    if (!todaySchedule || viewMode !== 'my') return null;

    for (const period of todaySchedule.periods) {
      const [startTime] = period.time.split('-');
      if (timeToMinutes(startTime) > timeToMinutes(currentTime)) {
        return period;
      }
    }
    return null;
  };

  const nextClass = getNextClass();

  const getOwnerLabel = () => {
    if (viewMode === 'my') {
      return `Teacher: ${teachers.find(t => t.id === myId)?.name || 'Teacher'}`;
    } else if (viewMode === 'class' && selectedClass) {
      return `Class: ${classes.find(c => c.id === selectedClass)?.name || selectedClass}`;
    }
    return 'Timetable';
  };


  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    // Using browser's print-to-PDF functionality
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(document.documentElement.outerHTML);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  // Show suspension warning if teacher is suspended
  if (isSuspended) {
    return <SuspensionWarning schoolName={suspendedSchoolName} isLoading={suspensionLoading} />;
  }

  if (isLoading || suspensionLoading) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <Teachersidebar />
          <div className="flex flex-col flex-1 h-full">
            <Teacherheader />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (networkError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <p className="text-lg font-semibold text-red-600">Network error occurred. Please check your internet connection and try again.</p>
        <Button onClick={() => { setIsLoading(true); setNetworkError(false); auth.currentUser?.getIdToken(true); }}>
          Retry
        </Button>
      </div>
    );
  }

  if (!hasValidClaims) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-lg font-semibold text-red-600">No valid permissions to view timetable.</p>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-sans overflow-hidden">
        <Teachersidebar />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <Teacherheader />
          <main className="flex-1 relative bg-[#FFFFFF] dark:bg-[#020817]">
            <div className="absolute inset-0 overflow-y-auto p-2">
            {/* Print-specific styles */}
            <style>{`
              @media print {
                @page {
                  size: A4 landscape;
                  margin: 0.8cm;
                }
                
                * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                
                body, html {
                  margin: 0 !important;
                  padding: 0 !important;
                  overflow: visible !important;
                  background: white !important;
                }
                
                /* Hide everything except print content */
                body * {
                  visibility: hidden !important;
                }
                
                .print-wrapper,
                .print-wrapper * {
                  visibility: visible !important;
                }
                
                .no-print, 
                .screen-only {
                  display: none !important;
                }
                
                .print-only {
                  display: block !important;
                }
                
                .print-wrapper {
                  position: absolute !important;
                  top: 0 !important;
                  left: 0 !important;
                  right: 0 !important;
                  width: 100% !important;
                  background: white !important;
                  z-index: 9999 !important;
                }
                
                .print-header {
                  text-align: center;
                  margin-bottom: 15px;
                  border-bottom: 2px solid #000;
                  padding-bottom: 10px;
                  background: white !important;
                }
                
                .print-footer {
                  position: fixed;
                  bottom: 0.8cm;
                  left: 0.8cm;
                  right: 0.8cm;
                  text-align: center;
                  font-size: 9px;
                  color: #333;
                  border-top: 1px solid #333;
                  padding-top: 8px;
                  background: white !important;
                }
                
                .print-timetable-container {
                  width: 100%;
                  margin: 0 auto;
                  background: white !important;
                }
                
                .print-timetable {
                  width: 100%;
                  border-collapse: collapse;
                  font-size: 10px;
                  table-layout: fixed;
                  background: white !important;
                }
                
                .print-timetable th {
                  background-color: #e5e7eb !important;
                  color: #111827 !important;
                  font-weight: bold;
                  padding: 10px 6px !important;
                  border: 1.5px solid #374151 !important;
                  text-align: center;
                  vertical-align: middle;
                }
                
                .print-timetable td {
                  border: 1px solid #6b7280 !important;
                  padding: 8px 5px !important;
                  vertical-align: top;
                  background-color: #ffffff !important;
                  color: #111827 !important;
                }
                
                .print-timetable td.time-cell {
                  background-color: #f3f4f6 !important;
                  color: #111827 !important;
                  font-weight: bold;
                  text-align: center;
                  font-size: 9px;
                }
                
                .print-period {
                  margin-bottom: 8px;
                  padding: 6px;
                  border-radius: 4px;
                  background-color: #ffffff !important;
                  border: 1px solid #d1d5db !important;
                  border-left: 3px solid #6b7280 !important;
                }
                
                .print-period:last-child {
                  margin-bottom: 0;
                }
                
                .print-period-subject {
                  font-weight: 700;
                  font-size: 10px;
                  margin-bottom: 3px;
                  color: #111827 !important;
                  background: transparent !important;
                }
                
                .print-period-class {
                  font-size: 9px;
                  color: #374151 !important;
                  margin-bottom: 2px;
                  font-weight: 500;
                  background: transparent !important;
                }
                
                .print-period-teacher {
                  font-size: 9px;
                  color: #374151 !important;
                  margin-bottom: 2px;
                  font-weight: 500;
                  background: transparent !important;
                }
                
                .print-period-room {
                  font-size: 8px;
                  color: #4b5563 !important;
                  font-weight: 400;
                  background: transparent !important;
                }
              }
              
              .print-only {
                display: none;
              }
              
              @media screen {
                .screen-only {
                  display: block;
                }
              }
            `}</style>

            {/* Print-only wrapper */}
            <div className="print-only print-wrapper">
              {/* Print-only header */}
              <div className="print-header">
                <h1 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '6px', color: '#000' }}>{schoolName}</h1>
                <h2 style={{ fontSize: '16px', marginBottom: '4px', color: '#000' }}>{getOwnerLabel()}</h2>
                <p style={{ fontSize: '12px', color: '#333' }}>{currentTerm} {currentYear}</p>
              </div>

              {/* Print-only timetable */}
              <div className="print-timetable-container">
                <table className="print-timetable">
                  <thead>
                    <tr>
                      <th style={{ width: '12%' }}>Time</th>
                      {currentSchedule.map((day) => (
                        <th key={day.day} style={{ width: '17.6%' }}>{day.day}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Get all unique time slots */}
                    {Array.from(new Set(currentSchedule.flatMap(day => day.periods.map(p => p.time))))
                      .sort()
                      .map((timeSlot) => (
                        <tr key={timeSlot}>
                          <td className="time-cell">
                            {timeSlot}
                          </td>
                          {currentSchedule.map((day) => {
                            const period = day.periods.find(p => p.time === timeSlot);
                            return (
                              <td key={day.day}>
                                {period ? (
                                  <div className="print-period">
                                    <div className="print-period-subject">{period.subject}</div>
                                    <div className={viewMode === 'my' ? "print-period-class" : "print-period-teacher"}>
                                      {viewMode === 'my' ? period.class : period.teacher || period.class}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '9px' }}>—</div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Print-only footer */}
              <div className="print-footer">
                Generated on: {now.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            </div>

            <div className="space-y-6 p-4 md:p-6 screen-only">
              {/* Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 no-print">
                <div className="flex items-center gap-3">
                  <Calendar className="h-6 w-6 md:h-7 md:w-7 text-blue-600 dark:text-blue-400" />
                  <h1 className="text-2xl md:text-3xl font-bold leading-tight text-gray-900 dark:text-white">Teaching Timetable</h1>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="font-semibold bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 px-4 py-2">
                      <Download className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Export Timetable</span>
                      <span className="sm:hidden">Export</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 w-48">
                    <DropdownMenuItem onClick={handlePrint} className="font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 px-3 py-2">
                      <Printer className="h-4 w-4 mr-3" />
                      Print (A4)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDownloadPDF} className="font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 px-3 py-2">
                      <FileDown className="h-4 w-4 mr-3" />
                      Download as PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {nextClass && viewMode === 'my' && (
                <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] border-l-4 border-l-blue-500 shadow-sm m-2 no-print">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Next Class</p>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{nextClass.subject}</h3>
                        <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span className="font-medium">{nextClass.time}</span>
                          </div>
                          <span className="font-medium">{nextClass.class}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2 no-print shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">View Options</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">View Mode</label>
                      <Select value={viewMode} onValueChange={(value: 'my' | 'class') => setViewMode(value)}>
                        <SelectTrigger className="font-medium bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                          <SelectItem value="my" className="font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700">My Timetable</SelectItem>
                          <SelectItem value="class" className="font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700">Class Timetable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {viewMode === 'class' && (
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Select Class</label>
                        <Select value={selectedClass} onValueChange={setSelectedClass}>
                          <SelectTrigger className="font-medium bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 h-10">
                            <SelectValue placeholder="Choose a class" />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                            {availableClasses.map(cls => (
                              <SelectItem key={cls.id} value={cls.id} className="font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700">{cls.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">View Type</label>
                      <Select value={viewType} onValueChange={(value: 'weekly' | 'daily') => setViewType(value)}>
                        <SelectTrigger className="font-medium bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                          <SelectItem value="weekly" className="font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700">Weekly View</SelectItem>
                          <SelectItem value="daily" className="font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700">Daily View</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {viewType === 'daily' && (
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Select Day</label>
                        <Select value={selectedDay} onValueChange={setSelectedDay}>
                          <SelectTrigger className="font-medium bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                            {days.map(day => (
                              <SelectItem key={day} value={day} className="font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700">{day}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {viewMode === 'class' && selectedClass && (
                <div className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] p-4 rounded-lg text-sm font-medium m-2 no-print shadow-sm">
                  Viewing timetable for <span className="font-semibold text-gray-900 dark:text-white">{classes.find(c => c.id === selectedClass)?.name}</span>
                </div>
              )}

              <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2 shadow-sm">
                <CardHeader className="pb-4 no-print">
                  <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                    {viewType === 'weekly' ? 'Week Schedule' : `${selectedDay} Schedule`}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {viewType === 'weekly' ? (
                    <div className="w-full">
                      {/* Mobile/Tablet View: Stacked Cards */}
                      <div className="grid lg:hidden grid-cols-1 sm:grid-cols-2 gap-4">
                        {currentSchedule.map((daySchedule) => (
                          <div
                            key={daySchedule.day}
                            className={`space-y-3 border rounded-lg p-4 shadow-sm transition-shadow hover:shadow-md ${daySchedule.day === currentDay ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]'}`}
                          >
                            <h3 className="font-bold text-base text-gray-900 dark:text-white text-center pb-3 border-b border-[#E2E8F0] dark:border-[#1E293B]">
                              {daySchedule.day}
                              {daySchedule.day === currentDay && (
                                <Badge variant="default" className="ml-2 text-xs font-semibold bg-blue-600 text-white">Today</Badge>
                              )}
                            </h3>
                            <div className="space-y-3">
                              {daySchedule.periods.map((period, idx) => {
                                const matchingSession = sessions.find(s => {
                                  const periodTimes = period.time.split('-');
                                  return s.startTime === periodTimes[0] && s.endTime === periodTimes[1];
                                });
                                const isBreak = matchingSession?.isBreak || matchingSession?.name.toLowerCase().includes('break') || matchingSession?.name.toLowerCase().includes('lunch');

                                if (isBreak) {
                                  return (
                                    <div key={idx} className="w-full p-3 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg my-2">
                                      <div className="flex items-center justify-center gap-2">
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                          <Clock className="w-4 h-4" />
                                          <span>{matchingSession?.name || period.subject || 'Break'}</span>
                                          <span className="text-xs">({period.time})</span>
                                        </div>
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div
                                    key={idx}
                                    className="p-3 rounded-lg border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] hover:shadow-sm transition-shadow my-2"
                                  >
                                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 mb-2">
                                      <Clock className="h-3 w-3 flex-shrink-0" />
                                      <span className="font-medium text-sm">{period.time}</span>
                                    </div>
                                    <Badge className={`${getSubjectColor(period.subject)} mb-2 text-xs font-semibold`}>
                                      {period.subject}
                                    </Badge>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{viewMode === 'my' ? period.class : period.teacher || period.class}</p>
                                  </div>
                                );
                              })}
                              {daySchedule.periods.length === 0 && (
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 text-center italic">No classes scheduled</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Desktop View: Grid Table */}
                      <div className="hidden lg:block overflow-x-auto">
                        <div className="min-w-[800px]">
                          <div className="grid grid-cols-6 gap-2">
                            {/* Header Row */}
                            <div className="font-mono font-bold text-xs text-gray-500 dark:text-gray-400 px-2 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center h-10">Time</div>
                            {days.map((day) => (
                              <div key={day} className={`font-bold text-sm text-center text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center h-10 ${day === currentDay ? 'border-2 border-blue-500' : ''}`}>
                                {day}
                              </div>
                            ))}

                            {/* Data Rows */}
                            {currentSchedule[0]?.periods.map((_, periodIndex) => {
                              const firstPeriod = currentSchedule[0].periods[periodIndex];
                              const matchingSession = sessions.find(s => {
                                const periodTimes = firstPeriod.time.split('-');
                                return s.startTime === periodTimes[0] && s.endTime === periodTimes[1];
                              });
                              const isBreak = matchingSession?.isBreak || matchingSession?.name.toLowerCase().includes('break') || matchingSession?.name.toLowerCase().includes('lunch');

                              if (isBreak) {
                                return (
                                  <div key={`break-${periodIndex}`} className="col-span-6 w-full p-3 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg my-1">
                                    <div className="flex items-center justify-center gap-2">
                                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <Clock className="w-4 h-4" />
                                        <span>{matchingSession?.name || 'Break'}</span>
                                        <span className="text-xs">({firstPeriod.time})</span>
                                      </div>
                                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div key={`row-${periodIndex}`} className="contents">
                                  {/* Time Column */}
                                  <div className="text-xs font-mono font-bold text-gray-500 dark:text-gray-400 py-2 px-2 flex items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
                                    {firstPeriod.time}
                                  </div>
                                  
                                  {/* Day Columns */}
                                  {days.map(day => {
                                    const daySchedule = currentSchedule.find(d => d.day === day);
                                    const period = daySchedule?.periods[periodIndex];
                                    
                                    return (
                                      <div key={`${day}-${periodIndex}`} className="p-2 rounded-lg border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] hover:shadow-md transition-shadow min-h-[80px]">
                                        {period && period.subject ? (
                                          <div className="flex flex-col h-full justify-between">
                                            <Badge className={`${getSubjectColor(period.subject)} mb-1 text-xs font-semibold w-fit`}>
                                              {period.subject}
                                            </Badge>
                                            <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                              {viewMode === 'my' ? period.class : period.teacher || period.class}
                                            </p>
                                          </div>
                                        ) : (
                                          <div className="h-full w-full flex items-center justify-center">
                                            <span className="text-gray-300 dark:text-gray-700 text-xs">-</span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {currentSchedule
                        .find(d => d.day === selectedDay)
                        ?.periods.map((period, idx) => {
                          // Check if this period is a break by matching time with sessions
                          const matchingSession = sessions.find(s => {
                            const periodTimes = period.time.split('-');
                            return s.startTime === periodTimes[0] && s.endTime === periodTimes[1];
                          });
                          const isBreak = matchingSession?.isBreak || matchingSession?.name.toLowerCase().includes('break') || matchingSession?.name.toLowerCase().includes('lunch');

                          // Render break sessions as horizontal banners
                          if (isBreak) {
                            return (
                              <div key={idx} className="w-full p-3 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg my-2">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                    <Clock className="w-4 h-4" />
                                    <span>{matchingSession?.name || period.subject || 'Break'}</span>
                                    <span className="text-xs">({period.time})</span>
                                  </div>
                                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div
                              key={idx}
                              className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 rounded-lg border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] hover:shadow-sm transition-shadow my-2"
                            >
                              <div className="flex items-center gap-2 min-w-[120px]">
                                <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400 flex-shrink-0" />
                                <span className="font-semibold text-gray-900 dark:text-white">{period.time}</span>
                              </div>
                              <Badge className={`${getSubjectColor(period.subject)} font-semibold`}>
                                {period.subject}
                              </Badge>
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 dark:text-white">{viewMode === 'my' ? period.class : period.teacher || period.class}</p>
                              </div>
                            </div>
                          );
                        }) || <p className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 italic">No classes scheduled for {selectedDay}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>

              {uniqueSubjects.length > 0 && (
                <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2 no-print shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-gray-900 dark:text-white">Subject Color Legend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {uniqueSubjects.map(subject => (
                        <Badge key={subject} className={`${getSubjectColor(subject)} font-medium text-sm px-3 py-1`}>
                          {subject}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default TeacherTimetable;